import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Bot, Send, Sparkles, UserCheck, Bell, MessageSquare, CheckCheck, Check, AlertTriangle,
  PauseCircle, PlayCircle, Paperclip, FileText, X, ArrowLeft, ArrowRight, BookOpen,
} from "lucide-react";
import { crmApi } from "../../lib/crmApi";
import { ImageGrid, Lightbox, MediaBody } from "./MediaMessages";
import { LibraryPicker, SuggestedRefs } from "./LibraryMediaTab";
import { ConceptPanel } from "./ConceptTasks";
import { ReplyFeedbackButton } from "./ReplyFeedback";
import { PageHead, Chip, inputCls, SERVICE_CATEGORIES, LEAD_STATUSES, LEAD_SCORES, label } from "./crmUi";

const AI_STATUSES = [
  ["auto_replied", "Auto replied", "#3F6E2C"],
  ["draft_waiting", "Draft waiting", "#A6822F"],
  ["human_takeover", "Human takeover", "#A3141A"],
  ["qualified", "Qualified lead", "#3F6E2C"],
  ["new", "New", "#7A6B5F"],
];

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "numeric", month: "short" });
};

function MonitorStrip() {
  const [m, setM] = useState(null);
  useEffect(() => {
    const load = () => crmApi.waMonitor().then(setM).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  if (!m) return null;
  const statusColor = { active: "#3F6E2C", paused: "#A6822F", error: "#A3141A" }[m.automation_status] || "#7A6B5F";
  const stats = [
    ["New today", m.new_wa_conversations_today, "monitor-new-today"],
    ["Auto replies", m.auto_replies_today, "monitor-auto-replies"],
    ["Qualified", m.qualified_leads_today, "monitor-qualified"],
    ["Alerts sent", m.alerts_sent_today, "monitor-alerts"],
    ["Need takeover", m.needs_human_takeover, "monitor-takeover"],
    ["Failed sends 24h", m.failed_sends_24h, "monitor-failed"],
    ["Pending follow-ups", m.pending_followups, "monitor-followups"],
    ["Unread", m.unread_messages, "monitor-unread"],
    ["Turn batches", m.batches_processed_today, "monitor-batches"],
    ["Dupes blocked", m.duplicates_suppressed_today, "monitor-dupes"],
  ];
  return (
    <div className="bg-[#1C110C] px-4 py-3 mb-4 flex items-center gap-4 flex-wrap" data-testid="wa-monitor-strip">
      <span
        className="text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 border inline-flex items-center gap-1.5"
        style={{ color: statusColor, borderColor: `${statusColor}66`, background: `${statusColor}15` }}
        data-testid="wa-automation-status"
      >
        ● Automation {m.automation_status} · {(m.auto_reply_mode || "").replace("_", " ")}
      </span>
      {stats.map(([l, v, tid]) => (
        <span key={l} className="text-[11px] text-[#EDE1C8]/70" data-testid={tid}>
          {l}: <strong className={`${v > 0 && (l === "Failed sends 24h" || l === "Need takeover" || l === "Unread") ? "text-[#E08080]" : "text-[#D4A963]"}`}>{v}</strong>
        </span>
      ))}
      {m.unanswered_over_threshold > 0 && (
        <span className="text-[10px] text-white bg-[#A3141A] px-2 py-0.5" data-testid="monitor-unanswered-warning">
          ⚠ {m.unanswered_over_threshold} customer(s) waiting &gt;90s for a reply
        </span>
      )}
      {m.median_response_ms != null && (
        <span className="text-[11px] text-[#EDE1C8]/70" data-testid="monitor-response-times">
          Response: median <strong className="text-[#D4A963]">{(m.median_response_ms / 1000).toFixed(1)}s</strong>
          {" · "}p95 <strong className="text-[#D4A963]">{(m.p95_response_ms / 1000).toFixed(1)}s</strong>
        </span>
      )}
      {m.sender_number && <span className="text-[10px] text-[#EDE1C8]/40 ml-auto">Sends from {m.sender_number} · alerts → {m.internal_alert_number}</span>}
    </div>
  );
}

const SENDER_BUBBLE = {
  customer: "bg-white border-[#6E1F2C]/15 mr-auto",
  ai: "bg-[#D4A963]/15 border-[#D4A963]/40 ml-auto",
  sales_staff: "bg-[#3F6E2C]/10 border-[#3F6E2C]/30 ml-auto",
  admin: "bg-[#6E1F2C]/10 border-[#6E1F2C]/30 ml-auto",
};

function DeliveryTicks({ status }) {
  if (!status) return null;
  if (status === "failed") return <AlertTriangle size={11} className="text-[#A3141A] inline" />;
  if (status === "read") return <CheckCheck size={12} className="text-[#2b7fd4] inline" />;
  if (status === "delivered") return <CheckCheck size={12} className="text-[#7A6B5F] inline" />;
  return <Check size={12} className="text-[#7A6B5F] inline" />;
}

function TracePanel({ leadId }) {
  const [trace, setTrace] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    crmApi.waTrace(leadId).then((r) => { setTrace(r); setErr(""); }).catch((e) => setErr(e.message));
  }, [leadId]);
  useEffect(() => { load(); }, [load]);
  if (err) return <p className="text-[10px] text-[#A3141A]">{err}</p>;
  if (!trace) return <p className="text-[10px] text-[#7A6B5F]">Loading trace…</p>;
  return (
    <div className="mt-2 space-y-2" data-testid="wa-pipeline-trace">
      <div className="text-[10px] flex justify-between gap-2">
        <span className="text-[#7A6B5F]">Qualified-lead alert</span>
        <span className={`text-right ${trace.alert.claimed && !trace.alert.sent ? "text-[#A3141A]" : "text-[#1C110C]"}`} data-testid="wa-trace-alert-status">
          {trace.alert.claimed
            ? trace.alert.sent
              ? `sent via ${trace.alert.channel} (${trace.alert.attempts} attempt${trace.alert.attempts > 1 ? "s" : ""})`
              : `FAILED × ${trace.alert.attempts} — auto-retry ${trace.alert.next_retry_at ? "at " + trace.alert.next_retry_at.slice(11, 16) + " UTC" : "pending"}`
            : "not triggered yet"}
        </span>
      </div>
      {trace.alert.error && <p className="text-[9px] text-[#A3141A] break-all">{trace.alert.error}</p>}
      {trace.batches.length === 0 && <p className="text-[10px] text-[#7A6B5F]">No AI batches processed yet.</p>}
      {trace.batches.map((b) => (
        <div key={b.batch_id} className="border border-[#6E1F2C]/15 p-2" data-testid="wa-trace-batch">
          <div className="flex justify-between text-[9px] uppercase tracking-[0.12em]">
            <span className={b.status === "completed" ? "text-[#2E5E3A]" : ["error", "send_failed"].includes(b.status) ? "text-[#A3141A]" : "text-[#A6822F]"}>
              {b.status}{b.total_ms ? ` · ${(b.total_ms / 1000).toFixed(1)}s total` : ""}
            </span>
            <span className="text-[#7A6B5F]">{b.created_at?.slice(11, 19)} UTC</span>
          </div>
          {b.error && <p className="text-[9px] text-[#A3141A] break-all mt-0.5">{b.error}</p>}
          <div className="mt-1 space-y-0.5">
            {b.events.map((ev, i) => (
              <div key={i} className="text-[9px]">
                <span className="text-[#7A6B5F]">{ev.at ? ev.at.slice(11, 19) : "·"}</span>{" "}
                <span className="text-[#6E1F2C] uppercase tracking-[0.08em]">{ev.stage.replace(/_/g, " ")}</span>
                <div className="text-[#1C110C] break-words pl-2">{ev.detail}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={load}
        className="border border-[#7A6B5F]/30 text-[#7A6B5F] px-2 py-1 text-[9px] uppercase tracking-[0.14em] hover:bg-[#7A6B5F]/10">
        ↻ Refresh trace
      </button>
    </div>
  );
}

function DiagnosticsPanel({ leadId }) {
  const [open, setOpen] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    crmApi.waDiagnostics(leadId).then((r) => { setD(r); setMsg(""); }).catch((e) => setMsg(e.message));
  }, [leadId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const retry = async () => {
    try { const r = await crmApi.waRetry(leadId); setMsg(r.detail); load(); }
    catch (e) { setMsg(`Retry failed: ${e.message}`); }
  };
  return (
    <div className="border-t border-[#6E1F2C]/10 pt-3" data-testid="wa-thread-diagnostics">
      <button type="button" data-testid="wa-diag-toggle" onClick={() => setOpen(!open)}
        className="text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C]">
        {open ? "▾" : "▸"} AI Diagnostics (admin)
      </button>
      {open && !d && <p className="text-[10px] text-[#7A6B5F] mt-1">Loading…</p>}
      {open && d && (
        <div className="mt-2 space-y-1 text-[10px]">
          {d.ai_last_skip_reason && (
            <p className="text-[#A3141A] bg-[#A3141A]/8 border border-[#A3141A]/20 px-2 py-1" data-testid="wa-diag-skip-reason">
              Last AI reply skipped: {d.ai_last_skip_reason}
            </p>
          )}
          {[
            ["Number", d.normalized_number],
            ["Mode", d.auto_reply_mode],
            ["AI paused", String(d.ai_paused)], ["Takeover", String(d.human_takeover)],
            ["Takeover reason", d.human_takeover_reason || "—"],
            ["Staff last manual reply", d.staff_last_manual_reply_at?.slice(0, 16) || "—"],
            ["Last inbound msg ID", d.last_inbound_wa_message_id?.slice(-14) || "—"],
            ["Last batch", `${d.last_batch_status || "—"} (${d.last_batch_message_count ?? 0} msgs)`],
            ["Timings", d.last_batch_timings_ms ? `wait ${d.last_batch_timings_ms.batch_wait_ms ?? "—"} · ai ${d.last_batch_timings_ms.ai_ms ?? "—"} · send ${d.last_batch_timings_ms.send_ms ?? "—"} · total ${d.last_batch_timings_ms.total_ms ?? "—"} ms` : "—"],
            ["Unprocessed msgs", d.unprocessed_customer_messages],
            ["Timer pending / lock", `${d.batch_timer_pending} / ${d.processing_lock_held}`],
            ["Last AI run", `${d.ai_last_run_started?.slice(11, 19) || "—"} → ${d.ai_last_run_ended?.slice(11, 19) || "—"}`],
            ["Model", d.ai_model],
            ["Last outbound", `${d.last_outbound_sender || "—"} · ${d.last_outbound_status || "—"} · ${d.last_outbound_wa_message_id?.slice(-12) || ""}`],
            ["Conversion score", d.conversion_score ?? "—"],
            ["Next best action", d.next_best_action || "—"],
            ["Retry count", d.batch_retry_count],
            ["Last error", d.last_error || "none"],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2">
              <span className="text-[#7A6B5F]">{k}</span>
              <span className="text-[#1C110C] text-right break-all">{String(v)}</span>
            </div>
          ))}
          <div className="flex gap-1.5 pt-1">
            <button type="button" data-testid="wa-diag-retry-btn" onClick={retry}
              className="border border-[#6E1F2C]/30 text-[#6E1F2C] px-2 py-1 text-[9px] uppercase tracking-[0.14em] hover:bg-[#6E1F2C]/10">
              Retry / reset AI processing
            </button>
            <button type="button" data-testid="wa-diag-trace-btn" onClick={() => setShowTrace(!showTrace)}
              className="border border-[#6E1F2C]/30 text-[#6E1F2C] px-2 py-1 text-[9px] uppercase tracking-[0.14em] hover:bg-[#6E1F2C]/10">
              {showTrace ? "Hide" : "Show"} pipeline trace
            </button>
            <button type="button" onClick={load}
              className="border border-[#7A6B5F]/30 text-[#7A6B5F] px-2 py-1 text-[9px] uppercase tracking-[0.14em] hover:bg-[#7A6B5F]/10">
              ↻ Refresh
            </button>
          </div>
          {showTrace && <TracePanel leadId={leadId} />}
          {msg && <p className="text-[#A6822F]" data-testid="wa-diag-msg">{msg}</p>}
        </div>
      )}
    </div>
  );
}

function ThreadView({ thread, staff, onChanged, onBack }) {
  const leadId = thread.lead_id;
  const [lead, setLead] = useState(null);
  const [convos, setConvos] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [showLibrary, setShowLibrary] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const reload = useCallback(() => {
    crmApi.getLead(leadId).then((d) => setLead(d.lead)).catch(() => {});
    crmApi.conversations(leadId).then((d) => setConvos(d.conversations)).catch(() => {});
  }, [leadId]);

  useEffect(() => {
    setLead(null); setConvos([]); setReply(""); setNote("");
    reload();
    crmApi.waMarkRead(leadId).then(onChanged).catch(() => {});
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [leadId, reload, onChanged]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [convos.length]);

  const act = async (fn, key, okMsg) => {
    setBusy(key); setNote("");
    try {
      const r = await fn();
      setNote(okMsg ? (typeof okMsg === "function" ? okMsg(r) : okMsg) : "");
      reload(); onChanged();
    } catch (e) { setNote(`Failed: ${e.message}`); }
    finally { setBusy(""); }
  };

  const patch = (changes) => act(() => crmApi.updateLead(leadId, changes), "patch");
  const sendReply = () => {
    if (attachments.length) {
      act(async () => {
        const r = await crmApi.waSendMedia(leadId, attachments, reply.trim());
        setAttachments([]); setReply("");
        return r;
      }, "send", (r) => (r.ok ? `Sent ${r.sent}/${r.total} ✓`
        : `Sent ${r.sent}/${r.total} — ${r.results.filter((x) => !x.ok).map((x) => `${x.filename}: ${x.error}`).join("; ")}`));
      return;
    }
    if (reply.trim()) {
      act(async () => {
        await crmApi.sendWhatsApp(leadId, reply.trim());
        setReply("");
      }, "send", "Sent ✓");
    }
  };
  const retryTranscription = (convId) => act(() => crmApi.retryTranscription(convId), "retry", "Transcription re-queued ✓");
  const addFiles = (e) => {
    setAttachments((a) => [...a, ...Array.from(e.target.files || [])].slice(0, 10));
    e.target.value = "";
  };
  const moveAttachment = (i, d) => setAttachments((a) => {
    const b = [...a];
    const [x] = b.splice(i, 1);
    b.splice(i + d, 0, x);
    return b;
  });
  const genDraft = () => act(async () => {
    const d = await crmApi.aiDraftReply(leadId);
    setReply(d.draft);
  }, "draft");
  const sendAlert = () => act(async () => {
    const r = await crmApi.waSendAlert(leadId);
    return r;
  }, "alert", (r) => (r.ok ? `Internal alert sent via ${r.channel} ✓` : `Alert failed: ${(r.errors || []).join("; ")}`));
  const summarise = () => act(() => crmApi.aiSummary(leadId), "summary");

  if (!lead) return <div className="flex-1 flex items-center justify-center text-xs text-[#7A6B5F]">Loading thread…</div>;

  const qualProgress = lead.qualification_progress || 0;

  // group consecutive image messages from the same sender into one WhatsApp-style grid
  const imageMsgs = convos.filter((c) => c.message_type === "image" && c.media_id);
  const rendered = [];
  convos.forEach((c) => {
    const last = rendered[rendered.length - 1];
    if (c.message_type === "image" && c.media_id && last?.grid && last.sender === c.sender_type) last.items.push(c);
    else if (c.message_type === "image" && c.media_id) rendered.push({ grid: true, sender: c.sender_type, items: [c], id: `g-${c.id}` });
    else rendered.push({ grid: false, c, id: c.id });
  });
  const hideRawText = (c) => c.message_type === "location"
    || (c.media_id && ["audio", "video", "document", "sticker"].includes(c.message_type));

  return (
    <div className="flex-1 flex flex-col min-w-0" data-testid="wa-thread-view">
      {/* Header */}
      <div className="border-b border-[#6E1F2C]/10 px-4 py-3 bg-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex items-center gap-2">
            <button
              type="button" data-testid="wa-thread-back-btn"
              onClick={onBack}
              className="md:hidden shrink-0 text-[#6E1F2C] border border-[#6E1F2C]/25 px-2 py-1 text-[10px] uppercase tracking-[0.12em]"
            >
              ← Inbox
            </button>
            <div className="min-w-0">
            <p className="text-[14px] font-medium text-[#1C110C] truncate" data-testid="wa-thread-name">
              {lead.customer_name || "Unknown customer"}
              <span className="text-[#7A6B5F] font-normal ml-2 text-[12px]">{lead.whatsapp_number}</span>
            </p>
            <p className="text-[10px] text-[#7A6B5F] mt-0.5">
              {lead.lead_ref} · {label(SERVICE_CATEGORIES, lead.service_category)} · source: {lead.source_channel || "—"}
              {lead.location ? ` · ${lead.location}` : ""}
              {" · "}
              <Link to={`/admin/crm/leads/${leadId}`} className="text-[#6E1F2C] hover:underline" data-testid="wa-thread-lead-link">Open full lead</Link>
            </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Chip list={LEAD_SCORES} value={lead.lead_score} testid="wa-thread-score" />
            <Chip list={LEAD_STATUSES} value={lead.lead_status} testid="wa-thread-status" />
            {lead.alert_sent && (
              <span className="text-[9px] tracking-[0.12em] uppercase px-2 py-0.5 border border-[#3F6E2C]/40 text-[#3F6E2C] bg-[#3F6E2C]/10" data-testid="wa-thread-alert-chip">
                Alert sent{lead.alert_channel ? ` (${lead.alert_channel.replace("whatsapp_", "wa ")})` : ""}
              </span>
            )}
          </div>
        </div>
        {/* Action row */}
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          <select
            data-testid="wa-thread-assign"
            className="bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-1.5 text-[10px]"
            value={lead.assigned_salesperson || ""}
            onChange={(e) => patch({ assigned_salesperson: e.target.value || null })}
          >
            <option value="">Assign staff…</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button
            type="button" data-testid="wa-thread-takeover-btn"
            onClick={() => patch({ human_takeover: !lead.human_takeover, ai_paused: !lead.human_takeover, lead_status: !lead.human_takeover ? "human_takeover" : lead.lead_status })}
            className={`px-2.5 py-1.5 text-[9px] tracking-[0.14em] uppercase border inline-flex items-center gap-1 ${lead.human_takeover ? "bg-[#A3141A] text-white border-[#A3141A]" : "border-[#A3141A]/40 text-[#A3141A] hover:bg-[#A3141A]/10"}`}
          >
            <UserCheck size={11} /> {lead.human_takeover ? "Takeover ON" : "Take over manually"}
          </button>
          <button
            type="button" data-testid="wa-thread-pause-ai-btn"
            onClick={() => patch({ ai_paused: !lead.ai_paused })}
            className={`px-2.5 py-1.5 text-[9px] tracking-[0.14em] uppercase border inline-flex items-center gap-1 ${lead.ai_paused ? "bg-[#7A6B5F] text-white border-[#7A6B5F]" : "border-[#7A6B5F]/40 text-[#7A6B5F] hover:bg-[#7A6B5F]/10"}`}
          >
            {lead.ai_paused ? <><PlayCircle size={11} /> Resume AI</> : <><PauseCircle size={11} /> Pause AI</>}
          </button>
          {[["qualified", "Mark qualified"], ["follow_up", "Needs follow-up"], ["lost", "Mark lost"]].map(([st, l]) => (
            <button
              key={st} type="button" data-testid={`wa-thread-mark-${st}`}
              onClick={() => patch({ lead_status: st })}
              className={`px-2.5 py-1.5 text-[9px] tracking-[0.14em] uppercase border ${lead.lead_status === st ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "border-[#6E1F2C]/30 text-[#6E1F2C] hover:bg-[#6E1F2C]/10"}`}
            >
              {l}
            </button>
          ))}
          <button
            type="button" data-testid="wa-thread-alert-btn"
            onClick={sendAlert} disabled={busy === "alert"}
            className="px-2.5 py-1.5 text-[9px] tracking-[0.14em] uppercase border border-[#D4A963] text-[#A6822F] hover:bg-[#D4A963]/15 inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Bell size={11} /> {busy === "alert" ? "Sending…" : "Send internal alert"}
          </button>
          {note && <span className={`text-[10px] ${note.startsWith("Failed") || note.includes("failed") ? "text-[#A3141A]" : "text-[#3F6E2C]"}`} data-testid="wa-thread-note">{note}</span>}
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Messages */}
        <div className="flex-1 flex flex-col min-w-0">
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-[#FAF5EC]" data-testid="wa-thread-messages">
            {rendered.map((r) => {
              if (r.grid) {
                const lastItem = r.items[r.items.length - 1];
                const captions = r.items
                  .map((c) => (c.message_text || "").replace(/^\[image (received|sent)\]\s*/, "").trim())
                  .filter((t) => t && !t.startsWith("[image"));
                const intent = r.items.find((c) => c.detected_intent)?.detected_intent;
                return (
                  <div key={r.id} className={`border px-2.5 py-2 max-w-[78%] ${SENDER_BUBBLE[r.sender] || SENDER_BUBBLE.customer}`}>
                    <p className="text-[9px] tracking-[0.14em] uppercase text-[#7A6B5F] mb-1">
                      {(r.sender || "").replace("_", " ")} · {r.items.length} image{r.items.length > 1 ? "s" : ""}
                    </p>
                    <ImageGrid items={r.items}
                      onOpen={(c) => setLightboxIdx(imageMsgs.findIndex((x) => x.id === c.id))} />
                    {captions.map((t, i) => <p key={i} className="text-[12px] text-[#1C110C] mt-1 whitespace-pre-wrap">{t}</p>)}
                    {intent && <p className="text-[10px] text-[#A6822F] mt-1" data-testid="wa-image-ai-intent">AI: {intent}</p>}
                    <p className="text-[9px] text-[#7A6B5F] mt-1 flex items-center gap-1 justify-end">
                      {fmtTime(lastItem.timestamp)} <DeliveryTicks status={lastItem.delivery_status} />
                    </p>
                  </div>
                );
              }
              const c = r.c;
              return (
                <div key={c.id} className={`border px-3 py-2 max-w-[78%] ${SENDER_BUBBLE[c.sender_type] || SENDER_BUBBLE.customer}`}>
                  <p className="text-[9px] tracking-[0.14em] uppercase text-[#7A6B5F] flex items-center gap-1.5">
                    {c.sender_type === "ai" && <Bot size={10} />}
                    {(c.sender_type || "").replace("_", " ")}
                    {c.message_type && c.message_type !== "text" && <span className="text-[#6E1F2C]">· {c.message_type}</span>}
                    {c.detected_intent && <span className="text-[#A6822F]">· {c.detected_intent}</span>}
                  </p>
                  <div className="mt-0.5">
                    <MediaBody c={c} onRetryTranscription={() => retryTranscription(c.id)} />
                    {!hideRawText(c) && <p className="text-[13px] text-[#1C110C] whitespace-pre-wrap">{c.message_text}</p>}
                  </div>
                  <div className="text-[9px] text-[#7A6B5F] mt-1 flex items-center gap-1 justify-end">
                    {c.sender_type === "ai" && <ReplyFeedbackButton leadId={leadId} conversationId={c.id} />}
                    {fmtTime(c.timestamp)} <DeliveryTicks status={c.delivery_status} />
                    {c.delivery_status === "failed" && c.delivery_error && <span className="text-[#A3141A]">{c.delivery_error.slice(0, 80)}</span>}
                  </div>
                </div>
              );
            })}
            {convos.length === 0 && <p className="text-xs text-[#7A6B5F] italic">No messages yet.</p>}
          </div>
          {lightboxIdx >= 0 && imageMsgs[lightboxIdx] && (
            <Lightbox images={imageMsgs} index={lightboxIdx} setIndex={setLightboxIdx} onClose={() => setLightboxIdx(-1)} />
          )}
          {showLibrary && <LibraryPicker leadId={leadId} onClose={() => setShowLibrary(false)} onSent={reload} />}
          <ConceptPanel leadId={leadId} onChanged={reload} />
          {/* Reply box */}
          <div className="border-t border-[#6E1F2C]/10 bg-white px-4 py-3 space-y-2">
            {attachments.length > 0 && (
              <div className="flex gap-2 flex-wrap items-start" data-testid="wa-attach-preview">
                {attachments.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative border border-[#6E1F2C]/20 p-1 w-24 bg-[#FAF5EC]">
                    {f.type.startsWith("image/")
                      ? <img src={URL.createObjectURL(f)} alt="" className="h-16 w-full object-cover" />
                      : <div className="h-16 flex items-center justify-center text-[#6E1F2C]"><FileText size={20} /></div>}
                    <p className="text-[8px] text-[#1C110C] truncate mt-0.5">{f.name}</p>
                    <button type="button" data-testid={`wa-attach-remove-${i}`}
                      onClick={() => setAttachments((a) => a.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 bg-[#A3141A] text-white p-0.5"><X size={9} /></button>
                    <div className="flex justify-between mt-0.5">
                      <button type="button" disabled={i === 0} onClick={() => moveAttachment(i, -1)}
                        className="text-[#7A6B5F] disabled:opacity-20"><ArrowLeft size={10} /></button>
                      <button type="button" disabled={i === attachments.length - 1} onClick={() => moveAttachment(i, 1)}
                        className="text-[#7A6B5F] disabled:opacity-20"><ArrowRight size={10} /></button>
                    </div>
                  </div>
                ))}
                <p className="text-[9px] text-[#7A6B5F] self-center">Message text below becomes the caption of the first file.</p>
              </div>
            )}
            {lead.ai_draft_reply && reply !== lead.ai_draft_reply && (
              <button
                type="button" data-testid="wa-thread-use-draft"
                onClick={() => setReply(lead.ai_draft_reply)}
                className="text-[10px] text-[#A6822F] hover:text-[#6E1F2C] inline-flex items-center gap-1"
              >
                <Bot size={11} /> AI draft waiting — click to load &amp; edit before sending
              </button>
            )}
            <div className="flex gap-2 items-end">
              <input type="file" ref={fileInputRef} multiple hidden onChange={addFiles}
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.zip,.rvt,.skp" />
              <button type="button" data-testid="wa-attach-btn" title="Attach files"
                onClick={() => fileInputRef.current?.click()}
                className="shrink-0 border border-[#6E1F2C]/25 text-[#6E1F2C] p-2 hover:bg-[#6E1F2C]/10 self-end mb-0.5">
                <Paperclip size={14} />
              </button>
              <button type="button" data-testid="wa-send-from-library-btn" title="Send from Library"
                onClick={() => setShowLibrary(true)}
                className="shrink-0 border border-[#A6822F]/40 text-[#A6822F] p-2 hover:bg-[#A6822F]/10 self-end mb-0.5">
                <BookOpen size={14} />
              </button>
              <textarea
                rows={2} data-testid="wa-thread-reply-input"
                className={inputCls}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                placeholder={attachments.length ? "Caption (optional) — Enter to send files" : "Type a WhatsApp reply… (Enter to send)"}
              />
              <div className="flex flex-col gap-1.5">
                <button
                  type="button" onClick={sendReply} disabled={busy === "send" || (!reply.trim() && !attachments.length)}
                  data-testid="wa-thread-send-btn"
                  className="bg-[#3F6E2C] text-white px-4 py-2 text-[9px] tracking-[0.16em] uppercase hover:bg-[#325a23] disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <Send size={11} /> {busy === "send" ? "…" : "Send"}
                </button>
                <button
                  type="button" onClick={genDraft} disabled={busy === "draft"}
                  data-testid="wa-thread-gen-ai-btn"
                  className="border border-[#D4A963] text-[#A6822F] px-4 py-2 text-[9px] tracking-[0.16em] uppercase hover:bg-[#D4A963]/15 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <Sparkles size={11} /> {busy === "draft" ? "…" : "AI reply"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* AI insight sidebar */}
        <div className="w-[270px] shrink-0 border-l border-[#6E1F2C]/10 bg-white overflow-y-auto hidden xl:block" data-testid="wa-thread-insights">
          <div className="p-4 space-y-4 text-[12px]">
            <div>
              <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-1.5">Qualification progress</p>
              <div className="h-1.5 bg-[#6E1F2C]/10 w-full">
                <div className="h-1.5 bg-[#3F6E2C]" style={{ width: `${qualProgress}%` }} />
              </div>
              <p className="text-[10px] text-[#7A6B5F] mt-1" data-testid="wa-thread-qual-progress">
                {qualProgress}%{lead.qualification_outcome && lead.qualification_outcome !== "not_yet" ? ` · ${lead.qualification_outcome.replace(/_/g, " ")}` : ""}
              </p>
              {(lead.qualification_missing || []).length > 0 && (
                <p className="text-[10px] text-[#7A6B5F] mt-1">Missing: {lead.qualification_missing.join(", ")}</p>
              )}
            </div>
            <div>
              <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-1.5">Extracted details</p>
              <dl className="space-y-1">
                {[
                  ["Service", label(SERVICE_CATEGORIES, lead.service_category)],
                  ["Location", lead.location],
                  ["Property", lead.property_type],
                  ["Scope", lead.sub_category],
                  ["Preferred time", lead.preferred_time],
                  ["Urgency", lead.urgency],
                  ["Budget", lead.budget_level],
                  ["Photos", lead.photos_received ? "Yes" : "No"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <dt className="text-[#7A6B5F]">{k}</dt>
                    <dd className="text-[#1C110C] text-right capitalize">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>
            {lead.requirement_summary && (
              <div>
                <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-1">Requirement</p>
                <p className="text-[#3E2A22]">{lead.requirement_summary}</p>
              </div>
            )}
            <div>
              <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-1">AI summary</p>
              {lead.conversation_summary
                ? <p className="text-[#3E2A22] whitespace-pre-wrap" data-testid="wa-thread-summary">{lead.conversation_summary}</p>
                : <p className="text-[#7A6B5F] italic text-[11px]">Not generated yet.</p>}
              <button
                type="button" onClick={summarise} disabled={busy === "summary"}
                data-testid="wa-thread-summarise-btn"
                className="mt-1.5 text-[9px] tracking-[0.16em] uppercase text-[#6E1F2C] border border-[#6E1F2C]/30 px-2 py-1 hover:bg-[#6E1F2C]/10 disabled:opacity-50"
              >
                {busy === "summary" ? "Thinking…" : "↻ Summarise"}
              </button>
            </div>
            {lead.ai_suggested_next_action && (
              <div>
                <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-1">Next recommended action</p>
                <p className="text-[#3E2A22]" data-testid="wa-thread-next-action">{lead.ai_suggested_next_action}</p>
              </div>
            )}
            <SuggestedRefs leadId={leadId} onOpenPicker={() => setShowLibrary(true)} />
            <DiagnosticsPanel leadId={leadId} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ConversationsPage() {
  const [threads, setThreads] = useState(null);
  const [staff, setStaff] = useState([]);
  const [err, setErr] = useState("");
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("lead");
  const [f, setF] = useState({ q: "", service: "", status: "", ai: "", staff: "", alert: "", days: "" });

  const loadInbox = useCallback(() => {
    crmApi.waInbox().then((d) => setThreads(d.threads)).catch((e) => setErr(e.message));
  }, []);

  useEffect(() => {
    loadInbox();
    crmApi.staff().then((d) => setStaff(d.staff)).catch(() => {});
    const t = setInterval(loadInbox, 20000);
    return () => clearInterval(t);
  }, [loadInbox]);

  const filtered = useMemo(() => {
    if (!threads) return [];
    const q = f.q.trim().toLowerCase();
    const cutoff = f.days ? new Date(Date.now() - Number(f.days) * 86400000).toISOString() : null;
    return threads.filter((t) =>
      (!q || `${t.customer_name || ""} ${t.whatsapp_number || ""} ${t.lead_ref || ""} ${t.last_message || ""}`.toLowerCase().includes(q))
      && (!f.service || t.service_category === f.service)
      && (!f.status || t.lead_status === f.status)
      && (!f.ai || t.ai_status === f.ai)
      && (!f.staff || t.assigned_salesperson === f.staff)
      && (!f.alert || (f.alert === "sent" ? t.alert_sent : !t.alert_sent))
      && (!cutoff || (t.last_at || "") >= cutoff));
  }, [threads, f]);

  const selected = useMemo(() => (threads || []).find((t) => t.lead_id === selectedId), [threads, selectedId]);

  return (
    <div data-testid="crm-conversations-page" className="flex flex-col h-[calc(100vh-140px)] min-h-[560px]">
      <PageHead eyebrow="WhatsApp" title="Conversations Inbox" />
      <MonitorStrip />
      {err && <p className="text-xs text-[#A3141A] mb-2">{err}</p>}

      <div className="flex-1 flex border border-[#6E1F2C]/10 bg-white min-h-0">
        {/* Thread list */}
        <div className={`w-full md:w-[340px] shrink-0 border-r border-[#6E1F2C]/10 flex flex-col min-h-0 ${selectedId ? "hidden md:flex" : "flex"}`}>
          <div className="p-2.5 border-b border-[#6E1F2C]/10 space-y-1.5">
            <input
              data-testid="wa-inbox-search"
              className={inputCls}
              value={f.q}
              onChange={(e) => setF({ ...f, q: e.target.value })}
              placeholder="Search name, number, message…"
            />
            <div className="grid grid-cols-3 gap-1">
              {[
                ["service", [["", "Service"], ...SERVICE_CATEGORIES]],
                ["status", [["", "Status"], ...LEAD_STATUSES.map(([k, l]) => [k, l])]],
                ["ai", [["", "AI status"], ...AI_STATUSES.map(([k, l]) => [k, l])]],
                ["staff", [["", "Staff"], ...staff.map((s) => [s.id, s.name])]],
                ["alert", [["", "Alert"], ["sent", "Alert sent"], ["not_sent", "No alert"]]],
                ["days", [["", "Any date"], ["1", "Today"], ["7", "7 days"], ["30", "30 days"]]],
              ].map(([key, opts]) => (
                <select
                  key={key}
                  data-testid={`wa-inbox-filter-${key}`}
                  className="bg-[#FAF5EC] border border-[#6E1F2C]/15 px-1.5 py-1.5 text-[10px] min-w-0"
                  value={f[key]}
                  onChange={(e) => setF({ ...f, [key]: e.target.value })}
                >
                  {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="wa-inbox-list">
            {!threads && <p className="text-xs text-[#7A6B5F] p-4">Loading…</p>}
            {threads && filtered.length === 0 && (
              <p className="text-xs text-[#7A6B5F] italic p-4">No conversations match. Threads appear when customers message +971 6 553 0401.</p>
            )}
            {filtered.map((t) => {
              const aiSt = AI_STATUSES.find(([k]) => k === t.ai_status);
              return (
                <button
                  key={t.lead_id}
                  type="button"
                  data-testid={`wa-inbox-thread-${t.lead_ref}`}
                  onClick={() => setParams({ lead: t.lead_id })}
                  className={`w-full text-left px-3 py-2.5 border-b border-[#6E1F2C]/8 hover:bg-[#FAF5EC] ${selectedId === t.lead_id ? "bg-[#FAF5EC]" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[12px] font-medium text-[#1C110C] truncate">
                      {t.customer_name || t.whatsapp_number || t.lead_ref}
                    </p>
                    <span className="text-[9px] text-[#7A6B5F] shrink-0">{fmtTime(t.last_at)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className={`text-[11px] truncate ${t.unread > 0 ? "text-[#1C110C] font-medium" : "text-[#7A6B5F]"}`}>
                      {t.last_sender === "customer" ? "" : "↩ "}{t.last_message || "…"}
                    </p>
                    {t.unread > 0 && (
                      <span className="shrink-0 bg-[#3F6E2C] text-white text-[9px] min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center" data-testid={`wa-inbox-unread-${t.lead_ref}`}>
                        {t.unread}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {aiSt && (
                      <span className="text-[8px] tracking-[0.1em] uppercase px-1.5 py-0.5 border" style={{ color: aiSt[2], borderColor: `${aiSt[2]}55`, background: `${aiSt[2]}10` }}>
                        {aiSt[1]}
                      </span>
                    )}
                    {t.service_category && (
                      <span className="text-[8px] tracking-[0.1em] uppercase px-1.5 py-0.5 border border-[#6E1F2C]/25 text-[#6E1F2C]">
                        {label(SERVICE_CATEGORIES, t.service_category)}
                      </span>
                    )}
                    <span className="text-[8px] tracking-[0.1em] uppercase px-1.5 py-0.5 border border-[#7A6B5F]/30 text-[#7A6B5F]">
                      {t.source_channel === "whatsapp" ? "WhatsApp" : t.source_channel === "website_form" ? "Website" : "Manual"}
                    </span>
                    {t.assigned_salesperson_name && (
                      <span className="text-[8px] text-[#A6822F]">→ {t.assigned_salesperson_name}</span>
                    )}
                    {t.alert_sent && <Bell size={9} className="text-[#3F6E2C]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Thread pane */}
        {selected ? (
          <ThreadView thread={selected} staff={staff} onChanged={loadInbox} onBack={() => setParams({})} />
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center flex-col gap-2 text-[#7A6B5F]">
            <MessageSquare size={28} className="opacity-30" />
            <p className="text-xs">Select a conversation to view the full thread</p>
          </div>
        )}
      </div>
    </div>
  );
}
